import { useCallback, useRef, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import type { CopilotStudioClient } from '@microsoft/agents-copilotstudio-client'
import type { Activity } from '@microsoft/agents-activity'
import {
  acquireCopilotToken,
  activityToChatMessage,
  createCopilotStudioClient,
  createLocalMessage,
  createOutgoingActivity,
  getStreamInfo,
} from '@/services/copilot/copilotService'
import {
  checkPersistence,
  createConversation,
  deleteConversation as deleteConversationApi,
  getConversation,
  listConversations,
  saveConversation,
} from '@/services/api/conversationsApi'
import type { ChatError, ChatMessage, ConnectionStatus } from '@/types/chat'
import type { ConversationSummary, StoredMessage } from '@/types/conversation'

export interface UseCopilotChatResult {
  status: ConnectionStatus
  messages: ChatMessage[]
  error: ChatError | null
  isBusy: boolean
  hasStarted: boolean
  /** Persisted conversation history for the signed-in user (empty when persistence is off). */
  conversations: ConversationSummary[]
  /** Id of the conversation currently shown, or null in single/in-memory mode. */
  activeConversationId: string | null
  /** True when the Cosmos-backed persistence layer is available. */
  persistenceEnabled: boolean
  connect: () => Promise<void>
  sendMessage: (text: string) => Promise<void>
  newConversation: () => Promise<void>
  selectConversation: (id: string) => Promise<void>
  removeConversation: (id: string) => Promise<void>
  retry: () => Promise<void>
}

const DEFAULT_TITLE = 'Conversație nouă'

/**
 * Owns the lifecycle of the user's Copilot Studio conversations: token
 * acquisition, per-conversation start/resume, streamed message send/receive,
 * and — when the backend is available — persistence to Cosmos DB so history
 * survives across sessions. The agent response remains the source of truth;
 * this hook only maps Activities to renderable ChatMessages.
 */
export function useCopilotChat(): UseCopilotChatResult {
  const { instance, accounts } = useMsal()
  const clientRef = useRef<CopilotStudioClient | null>(null)
  // Guards against React StrictMode invoking connect() twice.
  const initStartedRef = useRef(false)
  // True once a live server-side Copilot conversation exists for the active chat.
  const liveRef = useRef(false)
  // Copilot Studio conversation id backing the active chat (used to resume).
  const copilotConvIdRef = useRef<string | null>(null)
  // Id of the active persisted conversation document.
  const activeIdRef = useRef<string | null>(null)
  // Latest messages, kept in sync for persistence without awaiting a render.
  const messagesRef = useRef<ChatMessage[]>([])
  const persistenceRef = useRef(false)

  const [status, setStatus] = useState<ConnectionStatus>('idle')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [error, setError] = useState<ChatError | null>(null)
  const [hasStarted, setHasStarted] = useState(false)
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null)
  const [persistenceEnabled, setPersistenceEnabled] = useState(false)

  const updateMessages = useCallback(
    (updater: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => {
      setMessages((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater
        messagesRef.current = next
        return next
      })
    },
    [],
  )

  // Renders streamed responses live and captures the Copilot conversation id.
  const applyActivity = useCallback(
    (activity: Activity) => {
      const convId = activity.conversation?.id
      if (convId && !copilotConvIdRef.current) {
        copilotConvIdRef.current = convId
      }

      const text = activity.text ?? ''

      if (activity.type === 'typing') {
        const { streamId } = getStreamInfo(activity)
        if (!streamId || text.trim().length === 0) return
        updateMessages((prev) => upsertStreamingMessage(prev, streamId, text))
        return
      }

      if (activity.type !== 'message' || text.trim().length === 0) return

      const { streamId } = getStreamInfo(activity)
      updateMessages((prev) => finalizeMessage(prev, streamId, activity, text))
    },
    [updateMessages],
  )

  const ensureClient = useCallback(async (): Promise<CopilotStudioClient> => {
    if (clientRef.current) return clientRef.current
    const account = accounts[0]
    if (!account) throw new Error('Nu există un cont autentificat.')
    const token = await acquireCopilotToken(instance, account)
    clientRef.current = createCopilotStudioClient(token)
    return clientRef.current
  }, [accounts, instance])

  const upsertSummary = useCallback((summary: ConversationSummary) => {
    setConversations((prev) => [summary, ...prev.filter((c) => c.id !== summary.id)])
  }, [])

  const persistActive = useCallback(async () => {
    const account = accounts[0]
    if (!persistenceRef.current || !activeIdRef.current || !account) return
    try {
      const detail = await saveConversation(instance, account, activeIdRef.current, {
        title: deriveTitle(messagesRef.current),
        copilotConversationId: copilotConvIdRef.current,
        messages: serializeMessages(messagesRef.current),
      })
      upsertSummary(summaryOf(detail))
    } catch {
      // Non-fatal: the transcript is still shown locally for this session.
    }
  }, [accounts, instance, upsertSummary])

  // Starts a brand-new Copilot conversation (with greeting) and, when
  // persistence is on, creates its backing document.
  const beginNewConversation = useCallback(async () => {
    const client = await ensureClient()
    liveRef.current = false
    copilotConvIdRef.current = null
    activeIdRef.current = null
    setActiveConversationId(null)
    updateMessages([])
    setStatus('connecting')

    for await (const activity of client.startConversationStreaming(true)) {
      applyActivity(activity)
    }
    liveRef.current = true

    const account = accounts[0]
    if (persistenceRef.current && account) {
      const detail = await createConversation(instance, account, {
        title: DEFAULT_TITLE,
        copilotConversationId: copilotConvIdRef.current,
        messages: serializeMessages(messagesRef.current),
      })
      activeIdRef.current = detail.id
      setActiveConversationId(detail.id)
      upsertSummary(summaryOf(detail))
    }
    setStatus('connected')
  }, [accounts, applyActivity, ensureClient, instance, updateMessages, upsertSummary])

  // Loads an existing conversation's transcript and prepares it for resume.
  const openConversation = useCallback(
    async (id: string) => {
      const account = accounts[0]
      if (!account) return
      setStatus('connecting')
      const detail = await getConversation(instance, account, id)
      liveRef.current = false
      copilotConvIdRef.current = detail.copilotConversationId ?? null
      activeIdRef.current = detail.id
      setActiveConversationId(detail.id)
      updateMessages(deserializeMessages(detail.messages))
      setStatus('connected')
    },
    [accounts, instance, updateMessages],
  )

  const connect = useCallback(async () => {
    if (initStartedRef.current) return
    const account = accounts[0]
    if (!account) return
    initStartedRef.current = true
    setHasStarted(true)
    setStatus('connecting')
    setError(null)

    try {
      const persistence = await checkPersistence()
      persistenceRef.current = persistence
      setPersistenceEnabled(persistence)

      if (!persistence) {
        await beginNewConversation()
        return
      }

      const list = await listConversations(instance, account)
      setConversations(list)
      if (list.length === 0) {
        await beginNewConversation()
      } else {
        await openConversation(list[0].id)
      }
    } catch (err) {
      initStartedRef.current = false
      setStatus('error')
      setError({
        title: 'Nu s-a putut stabili conexiunea cu SOC-Copilot',
        detail: err instanceof Error ? err.message : 'Eroare necunoscută la conectare.',
        recoverable: true,
      })
    }
  }, [accounts, beginNewConversation, instance, openConversation])

  // Ensures a live server-side conversation, resuming a stored one when
  // possible, then streams the outgoing message's response.
  const runTurn = useCallback(
    async (client: CopilotStudioClient, outgoing: Activity) => {
      if (!liveRef.current && copilotConvIdRef.current) {
        try {
          for await (const activity of client.sendActivityStreaming(outgoing, copilotConvIdRef.current)) {
            applyActivity(activity)
          }
          liveRef.current = true
          return
        } catch {
          // Stored conversation expired server-side — start a fresh one.
          copilotConvIdRef.current = null
        }
      }

      if (!liveRef.current) {
        for await (const activity of client.startConversationStreaming(false)) {
          applyActivity(activity)
        }
        liveRef.current = true
      }

      for await (const activity of client.sendActivityStreaming(outgoing)) {
        applyActivity(activity)
      }
    },
    [applyActivity],
  )

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return

      let client: CopilotStudioClient
      try {
        client = await ensureClient()
      } catch (err) {
        setStatus('error')
        setError({
          title: 'SOC-Copilot nu a putut procesa mesajul',
          detail: err instanceof Error ? err.message : 'Eroare necunoscută.',
          recoverable: true,
        })
        return
      }

      updateMessages((prev) => [...prev, createLocalMessage('user', trimmed)])
      setStatus('sending')
      setError(null)

      try {
        await runTurn(client, createOutgoingActivity(trimmed))
        setStatus('connected')
        await persistActive()
      } catch (err) {
        setStatus('error')
        setError({
          title: 'SOC-Copilot nu a putut procesa mesajul',
          detail: err instanceof Error ? err.message : 'Eroare necunoscută la trimiterea mesajului.',
          recoverable: true,
        })
      }
    },
    [ensureClient, persistActive, runTurn, updateMessages],
  )

  const newConversation = useCallback(async () => {
    setError(null)
    try {
      await beginNewConversation()
    } catch (err) {
      setStatus('error')
      setError({
        title: 'Nu s-a putut porni o conversație nouă',
        detail: err instanceof Error ? err.message : 'Eroare necunoscută.',
        recoverable: true,
      })
    }
  }, [beginNewConversation])

  const selectConversation = useCallback(
    async (id: string) => {
      if (id === activeIdRef.current) return
      setError(null)
      try {
        await openConversation(id)
      } catch (err) {
        setStatus('error')
        setError({
          title: 'Nu s-a putut încărca conversația',
          detail: err instanceof Error ? err.message : 'Eroare necunoscută.',
          recoverable: true,
        })
      }
    },
    [openConversation],
  )

  const removeConversation = useCallback(
    async (id: string) => {
      const account = accounts[0]
      if (!account) return
      const remaining = conversations.filter((c) => c.id !== id)
      try {
        await deleteConversationApi(instance, account, id)
      } catch {
        return
      }
      setConversations(remaining)
      if (activeIdRef.current === id) {
        if (remaining.length > 0) {
          await openConversation(remaining[0].id)
        } else {
          await beginNewConversation()
        }
      }
    },
    [accounts, beginNewConversation, conversations, instance, openConversation],
  )

  const retry = useCallback(async () => {
    setError(null)
    if (!liveRef.current && messagesRef.current.length === 0) {
      initStartedRef.current = false
      await connect()
    } else {
      setStatus('connected')
    }
  }, [connect])

  const isBusy = status === 'connecting' || status === 'sending' || status === 'authenticating'

  return {
    status,
    messages,
    error,
    isBusy,
    hasStarted,
    conversations,
    activeConversationId,
    persistenceEnabled,
    connect,
    sendMessage,
    newConversation,
    selectConversation,
    removeConversation,
    retry,
  }
}

/** Derives a conversation title from the first user message (or a default). */
function deriveTitle(messages: ChatMessage[]): string {
  const firstUser = messages.find((m) => m.role === 'user')
  const text = firstUser?.text.trim()
  return text ? text.slice(0, 80) : DEFAULT_TITLE
}

function serializeMessages(messages: ChatMessage[]): StoredMessage[] {
  return messages
    .filter((m) => m.text.trim().length > 0)
    .map((m) => ({
      id: m.id,
      role: m.role,
      text: m.text,
      timestamp: m.timestamp.toISOString(),
    }))
}

function deserializeMessages(messages: StoredMessage[]): ChatMessage[] {
  return messages.map((m) => ({
    id: m.id,
    role: m.role,
    text: m.text,
    timestamp: new Date(m.timestamp),
    streaming: false,
  }))
}

function summaryOf(detail: {
  id: string
  title: string
  createdAt: string
  updatedAt: string
  messages?: StoredMessage[]
}): ConversationSummary {
  return {
    id: detail.id,
    title: detail.title,
    createdAt: detail.createdAt,
    updatedAt: detail.updatedAt,
    messageCount: detail.messages?.length ?? 0,
  }
}

/** Creates or updates the in-flight streamed agent message identified by streamId. */
function upsertStreamingMessage(prev: ChatMessage[], streamId: string, text: string): ChatMessage[] {
  const index = prev.findIndex((m) => m.streamId === streamId)
  if (index >= 0) {
    const next = [...prev]
    next[index] = { ...next[index], text }
    return next
  }
  return [
    ...prev,
    { id: `stream-${streamId}`, streamId, role: 'agent', text, timestamp: new Date(), streaming: true },
  ]
}

/** Finalizes the streamed message (or appends a fresh one for non-streamed replies). */
function finalizeMessage(
  prev: ChatMessage[],
  streamId: string | undefined,
  activity: Activity,
  text: string,
): ChatMessage[] {
  let index = streamId ? prev.findIndex((m) => m.streamId === streamId) : -1

  // Fallback: some replies omit the streamId on the terminal message — attach
  // it to the last still-streaming bubble instead of duplicating it.
  if (index < 0 && !streamId) {
    for (let i = prev.length - 1; i >= 0; i -= 1) {
      if (prev[i].streaming) {
        index = i
        break
      }
    }
  }

  if (index >= 0) {
    const next = [...prev]
    next[index] = { ...next[index], text, streaming: false }
    return next
  }

  const message = activityToChatMessage(activity)
  return message ? [...prev, message] : prev
}
